/**
 * Theme access for anything rendered inside ElfShell.
 *
 * The palette state lives in ElfShell, but the header sits several levels down
 * in each page. A context keeps it reachable without threading mode/setMode
 * through every page component — and lives in its own module so the header can
 * consume it without importing ElfShell (which would form a cycle).
 */

import React, { createContext, useContext, type ReactNode } from 'react';
import type { ElfMode } from './useElfPalette';

export interface ElfThemeValue {
  mode: ElfMode;
  setMode: (mode: ElfMode) => void;
  family: number;
  setFamily: (index: number) => void;
}

const ElfThemeContext = createContext<ElfThemeValue | null>(null);

export const ElfThemeProvider: React.FC<{ value: ElfThemeValue; children: ReactNode }> = ({
  value,
  children,
}) => <ElfThemeContext.Provider value={value}>{children}</ElfThemeContext.Provider>;

/** Returns null outside ElfShell, so headers can render without a provider. */
export function useElfTheme(): ElfThemeValue | null {
  return useContext(ElfThemeContext);
}
